import { useMemo } from 'react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';

interface StaffRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * Resolves a `subjectUserId` / `assignedReviewerId` / `resolvedById` /
 * `authorId` to a display name.
 *
 * `RiskEvent` and its notes carry only bare user ids — `RiskEventsService`
 * never includes a name, deliberately: the queue's own audit trail exists so
 * this module never has to widen an `include` on a table that already carries
 * an allegation. `GET /users` is a SEPARATE read, gated on its own
 * `users.view` — every role that holds `risk.review` (`BRANCH_MANAGER` and
 * both tenant-admin roles, per `common/constants/roles.ts`) holds it too, so
 * this never turns into a second permission gap on top of the one the route
 * already checked. A stale or deleted id that is not in this list still
 * renders — as the id itself — rather than disappearing.
 */
export function useStaffNames() {
  const { can } = usePermission();
  const canRead = can('users.view');
  const { data, loading } = useApi<unknown>(canRead ? '/users' : null, { limit: 200 });
  const rows = rowsOf<StaffRow>(data).rows;

  const byId = useMemo(() => new Map(rows.map((u) => [u.id, u])), [rows]);

  const displayName = (user: StaffRow): string =>
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.id;

  const nameOf = (id: string | null | undefined): string | null => {
    if (!id) return null;
    const user = byId.get(id);
    return user ? displayName(user) : id;
  };

  const options = useMemo(
    () => rows.map((u) => ({ id: u.id, name: displayName(u) })),
    [rows],
  );

  return { nameOf, options, loading, canRead };
}
